import { forwardRef, useState } from "react";
import ImageCropper, { getCroppedImg, HERO_ASPECT } from "./ImageCropper";
import { ModalPortal } from "../modal/modalPortal";
import { Button } from "../button/button";
import styled from "styled-components";
import { P } from "@vgrubs/components";

interface CropImageInputProps {
    onChange: (image: File | Blob | string | null) => void;
    tooltip?: string;
    /** Width / height of the crop box. Defaults to the PDF hero banner. */
    aspect?: number;
    /** `round` draws a circular mask; the file itself is still a square. */
    cropShape?: 'rect' | 'round';
    /**
     * Above `ModalPortal`'s own default of 130, because this is almost always
     * opened FROM a modal - a group's photo, a brand template's hero. At the
     * same z-index the crop overlay lands underneath the modal that opened it,
     * so that one stays undimmed and the two read as one flat stack.
     */
    zIndex?: number;
}

export const CropImageInput = forwardRef<HTMLInputElement, CropImageInputProps>(
    (props, ref) => {
        const [imageSrc, setImageSrc] = useState<string | null>(null);
        const [open, setOpen] = useState(false);
        const [croppedArea, setCroppedArea] =
            useState<{ x: number; y: number; width: number; height: number } | null>(
                null
            );

        const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = () => {
                setImageSrc(reader.result as string);
                setOpen(true);
            };
            reader.readAsDataURL(file);

            // Reset, so picking the same file again still fires onChange.
            e.target.value = '';
        };

        const handleSave = async () => {
            if (!imageSrc || !croppedArea) return;

            const croppedBlob = await getCroppedImg(imageSrc, croppedArea);

            props.onChange(croppedBlob);

            setOpen(false);
        };

        return (
            <>
                <input
                    ref={ref}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onFileSelect}
                />

                <ModalPortal isOpen={open} closeModal={() => setOpen(false)}
                    header="Crop Image"
                    zIndex={props.zIndex ?? 140}
                >
                    <Container>
                        <ImageCropper
                            image={imageSrc}
                            aspect={props.aspect ?? HERO_ASPECT}
                            cropShape={props.cropShape}
                            onCropComplete={(croppedAreaPixels) => {
                                setCroppedArea(croppedAreaPixels);
                            }}
                        />
                    </Container>
                    {props.tooltip && <P $mb="10px">
                        {props.tooltip}
                    </P>}
                    <Button $selected $w="100%" onClick={handleSave}>Save</Button>
                </ModalPortal>
            </>
        );
    }
);

const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    padding-bottom: 10px;
`;
